// GerarPropostaPDF.tsx
import React, { useState } from "react";
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import { Modal, Button, Form } from "react-bootstrap";
import { ProdutoOportunidade } from "../../../../types/orcamento";
import { apiService } from "../../../../services/api";
import { PropostaComercialDocument } from "./PropostaComercialDocument";
import { toast } from "react-toastify";

interface GerarPropostaPDFProps {
    opportunityId: number;
    produtos: ProdutoOportunidade[];
    formatCurrency: (value: number | string) => string;
}

const GerarPropostaPDF: React.FC<GerarPropostaPDFProps> = ({
    opportunityId,
    produtos,
    formatCurrency
}) => {
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [produtosSelecionados, setProdutosSelecionados] = useState<number[]>([]);
    
    // Caminho para o logo local 
    const logoPath = '/assets/img/A2R_logo.png';

    // Função para obter o valor prioritário (valor_proposta ou valor_referencia)
    const obterValorPrioritario = (produto: ProdutoOportunidade): number => {
        return produto.valor_proposta && produto.valor_proposta > 0 ? produto.valor_proposta : produto.valor_referencia;
    };

    const formatDate = (date?: string) => {
        if (!date) {
            // Usar data atual no fuso horário local do Brasil
            const today = new Date();
            const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
            
            // Formatar data para DD DE MÊS DE YYYY em português e maiúsculas
            const months = ['JANEIRO', 'FEVEREIRO', 'MARÇO', 'ABRIL', 'MAIO', 'JUNHO', 
                'JULHO', 'AGOSTO', 'SETEMBRO', 'OUTUBRO', 'NOVEMBRO', 'DEZEMBRO'];
                
            return `${d.getDate()} DE ${months[d.getMonth()]} DE ${d.getFullYear()}`;
        }
        
        // Se uma data for passada, garante que seja processada no fuso horário local
        const d = new Date(date);
        // Formatar data para DD DE MÊS DE YYYY em português e maiúsculas
        const months = ['JANEIRO', 'FEVEREIRO', 'MARÇO', 'ABRIL', 'MAIO', 'JUNHO', 
            'JULHO', 'AGOSTO', 'SETEMBRO', 'OUTUBRO', 'NOVEMBRO', 'DEZEMBRO'];
            
        return `${d.getDate()} DE ${months[d.getMonth()]} DE ${d.getFullYear()}`;
    };
    
    // Função para formatar quantidade sem pontos ou vírgulas (somente número inteiro)
    const formatQuantity = (quantidade: number): string => {
        return Math.floor(quantidade).toString();
    };

    // Função para ordenar produtos por número de edital ou id
    const ordenarProdutos = (produtosParaOrdenar: ProdutoOportunidade[]) => {
        return [...produtosParaOrdenar].sort((a, b) => {
            // Se ambos têm número de edital, comparar esses números
            if (a.numero_edital && b.numero_edital) {
                return a.numero_edital.localeCompare(b.numero_edital);
            }
            
            // Se apenas um tem número de edital, esse vem primeiro
            if (a.numero_edital && !b.numero_edital) {
                return -1;
            }
            
            if (!a.numero_edital && b.numero_edital) {
                return 1;
            }
            
            // Se nenhum tem número de edital, ordenar pelo ID
            return a.id - b.id;
        });
    };

    // Função para abrir o modal de seleção
    const handleAbrirModal = () => {
        // Por padrão, selecionar todos os produtos
        setProdutosSelecionados(produtos.map(p => p.id));
        setShowModal(true);
    };

    // Função para alternar seleção de um produto
    const toggleProdutoSelecionado = (produtoId: number) => {
        setProdutosSelecionados(prev => {
            if (prev.includes(produtoId)) {
                return prev.filter(id => id !== produtoId);
            } else {
                return [...prev, produtoId];
            }
        });
    };

    // Função para selecionar/deselecionar todos
    const toggleSelecionarTodos = () => {
        if (produtosSelecionados.length === produtos.length) {
            // Se todos estão selecionados, deselecionar todos
            setProdutosSelecionados([]);
        } else {
            // Se nem todos estão selecionados, selecionar todos
            setProdutosSelecionados(produtos.map(p => p.id));
        }
    };

    // Função para gerar a proposta com os produtos selecionados
    const handleGerarProposta = async () => {
        if (produtosSelecionados.length === 0) {
            toast.warning("Selecione pelo menos um produto para gerar a proposta.");
            return;
        }

        try {
            setLoading(true);
            
            // Filtrar apenas os produtos selecionados
            const produtosFiltrados = produtos.filter(p => produtosSelecionados.includes(p.id));
            
            // Buscar dados da oportunidade
            const opportunityResponse = await apiService.getOpportunityById(opportunityId);
            if (!opportunityResponse.status || !opportunityResponse.data.length) {
                throw new Error("Não foi possível carregar os dados da oportunidade");
            }
            
            const opportunity = opportunityResponse.data[0];
            
            // Buscar dados do município/destinatário
            const municipioResponse = await apiService.getMunicipios({ id: opportunity.id_municipio });
            if (!municipioResponse.success || !municipioResponse.data.length) {
                throw new Error("Não foi possível carregar os dados do município");
            }
            
            const municipio = municipioResponse.data[0];
            
            // Ordenar produtos para a proposta
            const produtosOrdenados = ordenarProdutos(produtosFiltrados);
            
            // Calcular valor total baseado nos valores prioritários (valor_proposta ou valor_referencia)
            const valorTotal = produtosOrdenados.reduce((total, produto) => {
                const valorPrioritario = obterValorPrioritario(produto);
                return total + (produto.quantidade * valorPrioritario);
            }, 0);
            
            // Dados da empresa
            const dadosEmpresa = {
                nome: "A2R BRASIL LTDA",
                cnpj: "45.458.851/0001-10",
                ie: "387.428.863.111",
                endereco: "Rua Antonio Tocachelli, 35",
                cidade: "Vila Lucinda/Romana - Itu",
                assinatura: "Itu",
                estado: "SP",
                cep: "13.309-722",
                telefone: "(11) 9 9974-8548",
                email: "ADMINISTRATIVO@A2RBRASIL.COM.BR"
            };
            
            // Contador para itens sem número de edital
            let itemSemNumeroEditalContador = 1;

            // Formatar os itens para o documento PDF
            const itensPDF = produtosOrdenados.map(produto => {
                // Usar número do edital se existir, caso contrário usar numeração sequencial
                const itemNumber = produto.numero_edital || (itemSemNumeroEditalContador++).toString();
                const valorPrioritario = obterValorPrioritario(produto);

                // Separar nome do produto e descrição específica
                const nomeProduto = produto.marca
                    ? `${produto.marca} - ${produto.nome_produto}`
                    : produto.nome_produto;

                const descricaoEspecifica = produto.descricao_especifica
                    ? `Descrição: ${produto.descricao_especifica}`
                    : '';

                return {
                    item: itemNumber,
                    nomeProduto: nomeProduto,
                    descricaoEspecifica: descricaoEspecifica,
                    quantidade: formatQuantity(produto.quantidade), // Formatação para número inteiro
                    valorUnitario: formatCurrency(valorPrioritario), // Usando valor prioritário
                    valorTotal: formatCurrency(produto.quantidade * valorPrioritario) // Usando valor prioritário
                };
            });
            
            // Dados para a proposta
            const dadosProposta = {
                empresa: dadosEmpresa.nome,
                cnpj: dadosEmpresa.cnpj,
                ie: dadosEmpresa.ie,
                endereco: dadosEmpresa.endereco,
                cidade: dadosEmpresa.cidade,
                assinatura: dadosEmpresa.assinatura,
                estado: dadosEmpresa.estado,
                cep: dadosEmpresa.cep,
                telefone: dadosEmpresa.telefone,
                email: dadosEmpresa.email,
                logoUrl: logoPath,
                
                destinatario: opportunity.titulo.toUpperCase(),
                pregao: `PREGÃO ELETRÔNICO`,
                processo: `PROCESSO ADMINISTRATIVO`,
                
                itens: itensPDF,
                valorTotal: formatCurrency(valorTotal), // Sem hífens
                validadeProposta: "CONFORME EDITAL",
                prazoEntrega: "CONFORME EDITAL",
                dadosBancarios: "BANCO SANTANDER - AG:2990 / CC: 12.026588-2",
                dataEmissao: formatDate() // Sem passar data para usar o dia atual
            };
            
            // Gerar o PDF como blob
            const blob = await pdf(<PropostaComercialDocument data={dadosProposta} />).toBlob();
            
            // Salvar o arquivo PDF
            saveAs(blob, `proposta_${opportunity.id}_${new Date().toISOString().split('T')[0]}.pdf`);
            
            toast.success(`Proposta Comercial gerada com sucesso! (${produtosFiltrados.length} produtos incluídos)`);
            setShowModal(false);
        } catch (error) {
            console.error("Erro ao gerar proposta:", error);
            toast.error("Erro ao gerar proposta. Por favor, tente novamente.");
        } finally {
            setLoading(false);
        }
    };
    
    // Calcular valor total dos produtos selecionados usando valores prioritários
    const valorTotalSelecionados = produtos
        .filter(p => produtosSelecionados.includes(p.id))
        .reduce((total, produto) => {
            const valorPrioritario = obterValorPrioritario(produto);
            return total + (produto.quantidade * valorPrioritario);
        }, 0);

    return (
        <>
            <button
                className="btn btn-primary"
                onClick={handleAbrirModal}
                disabled={produtos.length === 0}
            >
                <i className="ti ti-file-invoice me-2"></i>
                Gerar Proposta Inicial
            </button>

            {/* Modal de seleção de produtos */}
            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                size="lg"
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="ti ti-file-invoice me-2"></i>
                        Selecionar Produtos para Proposta
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">
                                Produtos disponíveis ({produtos.length} total)
                            </h6>
                            <div className="d-flex align-items-center gap-3">
                                <span className="text-muted small">
                                    {produtosSelecionados.length} selecionados
                                </span>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={toggleSelecionarTodos}
                                >
                                    {produtosSelecionados.length === produtos.length ? 
                                        'Deselecionar Todos' : 'Selecionar Todos'
                                    }
                                </Button>
                            </div>
                        </div>
                        
                        {produtosSelecionados.length > 0 && (
                            <div className="alert alert-info">
                                <strong>Valor total dos produtos selecionados:</strong> {formatCurrency(valorTotalSelecionados)}
                            </div>
                        )}
                    </div>

                    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <table className="table table-sm">
                            <thead className="table-light sticky-top">
                                <tr>
                                    <th style={{ width: '40px' }}>
                                        <Form.Check
                                            type="checkbox"
                                            checked={produtosSelecionados.length === produtos.length && produtos.length > 0}
                                            onChange={toggleSelecionarTodos}
                                            disabled={produtos.length === 0}
                                        />
                                    </th>
                                    <th>N° Edital</th>
                                    <th>Produto</th>
                                    <th>Qtd.</th>
                                    <th>Valor Unit.</th>
                                    <th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ordenarProdutos(produtos).map((produto) => (
                                    <tr 
                                        key={produto.id}
                                        className={produtosSelecionados.includes(produto.id) ? 'table-primary' : ''}
                                    >
                                        <td>
                                            <Form.Check
                                                type="checkbox"
                                                checked={produtosSelecionados.includes(produto.id)}
                                                onChange={() => toggleProdutoSelecionado(produto.id)}
                                            />
                                        </td>
                                        <td>
                                            <span className="badge bg-secondary">
                                                {produto.numero_edital || `Item ${produto.id}`}
                                            </span>
                                        </td>
                                        <td>
                                            <div 
                                                style={{
                                                    maxWidth: '200px',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                                title={produto.descricao_especifica ? `${produto.nome_produto} - ${produto.descricao_especifica}` : produto.nome_produto}
                                            >
                                                {produto.descricao_especifica ? `${produto.nome_produto} - ${produto.descricao_especifica}` : produto.nome_produto}
                                            </div>
                                            {produto.marca && (
                                                <small className="text-muted d-block">
                                                    Marca: {produto.marca}
                                                </small>
                                            )}
                                        </td>
                                        <td>{formatQuantity(produto.quantidade)}</td>
                                        <td>{formatCurrency(obterValorPrioritario(produto))}</td>
                                        <td>
                                            <strong>
                                                {formatCurrency(produto.quantidade * obterValorPrioritario(produto))}
                                            </strong>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {produtos.length === 0 && (
                        <div className="text-center text-muted py-4">
                            <i className="ti ti-package-off fs-48 mb-3"></i>
                            <p>Nenhum produto disponível para incluir na proposta.</p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <div className="text-muted small">
                            {produtosSelecionados.length > 0 ? 
                                `${produtosSelecionados.length} produto(s) selecionado(s)` : 
                                'Nenhum produto selecionado'
                            }
                        </div>
                        <div className="d-flex gap-2">
                            <Button
                                variant="secondary"
                                onClick={() => setShowModal(false)}
                                disabled={loading}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleGerarProposta}
                                disabled={loading || produtosSelecionados.length === 0}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Gerando Proposta...
                                    </>
                                ) : (
                                    <>
                                        <i className="ti ti-file-download me-2"></i>
                                        Gerar Proposta ({produtosSelecionados.length})
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default GerarPropostaPDF;